import { useState, useEffect, useRef } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'next-i18next'
import { useCookies } from 'react-cookie'

// Libs
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { useProductData } from '@/hooks/useProductData'
import { handleCartDataUpdate } from '@/methods/handleCartDataUpdate'
import { setUserRecord } from '@/api/setUserRecord'
import { handleLoginOrRegister } from '@/methods/handleLoginOrRegister'
import fastCheckout from '@/methods/fastCheckout'
import setBuyNowCacheData from '@/methods/setBuyNowCacheData'
import { handleAddCartMessage } from '@/methods/message'
import { getDisplayPriceWithRange } from '@/methods/getDisplayPrice'
import useFullScreen from '@/hooks/useFullScreen'
import { isClientSideShopApp } from '@/utils/navigator'
import { handleGtmViewItemList } from '@/methods/handleGtmEvent'
import { useObserver } from '@/hooks/useObserver'

// Components
import RoomProduct from './components/RoomProduct'
import HostProduct from './components/HostProduct'
import Popup from '@/components/common/Popup'
import SpecContent from '@/components/shared/SpecContent'

// Constants
import { CartAction } from '@/constants/cart'
import { socialReferralCookieKey } from '@/constants/cookieKey'
import { GTM_LOCATION, GtmItemListName, GtmUpdateCartAction } from '@/constants/gtmEvent'
import { QUERY_KEY } from '@/constants/queryKey'
import { THandleGtmUpdateCart } from '@/methods/handleGtmEvent'
import { isLiveMallClub } from '@/constants/systemType'

// Types
import { ProductInfoType } from '@/api/fetchProductData'

// Styles
import styles from './style.module.scss'
import { safeUrl } from '@/methods/url/safeUrl'
import { LIVE_TRACK_EVENT, trackLiveEvent } from '@/methods/liveTracking'

const cx = classnames.bind(styles)

interface ProductListProps {
  className: string
  productList: any
  isLive: boolean
  isHost?: boolean
  isFuture?: boolean
  isLessThenOneHour: boolean
  videoRef?: any
}

const SPEC_ACTION = {
  ADD_CART: 'ADD_CART',
  BUY_NOW: 'BUY_NOW',
}

const defaultProductData = { productId: '', specPopupAction: '' }

const ProductList = (props: ProductListProps) => {
  const { className, productList, isLive, isHost = false, isFuture, isLessThenOneHour } = props

  const { t } = useTranslation()

  const [currentProductList, setCurrentProductList] = useState<any[]>([])
  const [selectedProductData, setSelectedProductData] = useState(defaultProductData)
  const [productAmount, setProductAmount] = useState(0)

  const specPopupRef = useRef<any>(null)
  const loginPopupRef = useRef<any>(null)

  const router = useRouter()
  // 直播間 app 是原生
  const isNativeApp = router.query.mode === '1'

  const setUserRecordMutation = useMutation(setUserRecord)

  const isShowHostProduct = isHost && (isLive || (isFuture && isLessThenOneHour))

  const roomId = useAppSelector((state) => state.room.roomData.roomId)
  const socketData = useAppSelector((state) => state.socket)
  const memberData = useAppSelector((state) => state.member?.memberData)
  const fixedPlayerData = useAppSelector((state) => state.common.fixedPlayerData)
  const shoppingcartId = useAppSelector((state) => state.shoppingCart.shoppingcartId)

  const { memberInfoId, nickName, headshotUrl, role } = memberData

  const isLogin = !!memberInfoId

  const dispatch = useAppDispatch()

  const { isFullScreen } = useFullScreen()

  const { socket, stateData, userToken } = socketData
  const { topProductList, publishProductList } = stateData

  const { menu, searchKey, productInfo, skuItemInfo, filteredItems, handleSearchKeyChange } = useProductData(selectedProductData.productId)
  const { displayPrice } = getDisplayPriceWithRange({ productInfo, filteredItems, isLive })
  const queryClient = useQueryClient()

  // 加入購物車回報庫存不足時重拉商品資料，讓規格選擇器同步最新庫存
  const handleStockInsufficient = () => queryClient.invalidateQueries(QUERY_KEY.FETCH_PRODUCT_DATA)
  const selectedProductDataId = selectedProductData?.productId

  const popupContainer: any = typeof window === 'undefined' ? null : document.getElementById('playerVideo')

  const [cookies] = useCookies([socialReferralCookieKey])
  const referralFrom = cookies?.[socialReferralCookieKey]?.socialReferralFrom || ''
  const referralId = cookies?.[socialReferralCookieKey]?.socialReferralId || ''

  const isProductListSortedRef = useRef(false)
  const isIntersecCb = (entries: IntersectionObserverEntry[]) => {
    if (isProductListSortedRef.current === true) {
      const isIntersectingEntries = entries.filter((entry: IntersectionObserverEntry) => entry.isIntersecting)
      const dataAttributes = isIntersectingEntries.map((entry) => (entry.target as any).dataset)
      const productIdList = dataAttributes.map((item) => Number(item.id))
      if (currentProductList.length > 0) {
        const intersectingProductList = currentProductList
          .filter((currentProductItem: { productInfo: ProductInfoType }) => productIdList.includes(currentProductItem.productInfo.productId))
          .map((item) => item.productInfo)
          // 隱藏商品不進 GA4 電商資料
          .filter((itemProductInfo: ProductInfoType) => !itemProductInfo.isHidden)

        if (intersectingProductList.length > 0) {
          handleGtmViewItemList({
            itemListName: GtmItemListName.LIVE_ROOM,
            itemListId: roomId,
            productInfoList: intersectingProductList,
          })
        }
      }
    }
  }

  // PChome 合作案，只綁在合作案的立即購買按鈕上
  const pipWithWinOpen = async (url: string) => {
    const { videoRef } = props
    try {
      if (videoRef?.current !== document.pictureInPictureElement && videoRef?.current.readyState >= 3) {
        await requestPictureInPictureWithTimeout()
      }
    } catch (e) {
      console.log('pip error', e)
    }

    const dom = document.createElement('a')
    dom.href = safeUrl(url)
    dom.target = '_blank'
    dom.rel = 'noopener noreferrer'
    document.body.appendChild(dom)
    dom.click()
  }

  const requestPictureInPictureWithTimeout = async () => {
    const { videoRef } = props
    const pip = videoRef.current.requestPictureInPicture()
    Promise.race([pip, new Promise((resolve) => setTimeout(() => resolve('pip request time out'), 2000))])
      .then((result) => {
        if (result instanceof PictureInPictureWindow) {
          //toast.success('pip成功')
        } else {
          toast.error('畫面切換失敗!直播重載!', { autoClose: 3000 })
          setTimeout(() => {
            window.location.reload()
          }, 3000)
        }
      })
      .catch((e) => {
        console.error('PIP err:', e)
        toast.error('PIP開啟失敗')
      })
  }

  const handlePromotionMessage = (currentProduct: any) => {
    const userData = { nickName, headshotUrl, role, userToken: userToken || shoppingcartId } // userToken 有登入就用 userToken 沒登入就用 shoppingcartId

    handleAddCartMessage({ socket, item: currentProduct, userData })
  }

  const { observerRef } = useObserver({ observerCallback: isIntersecCb, dep: [currentProductList] })
  useEffect(() => {
    if (skuItemInfo?.length > 0 && !!selectedProductData.productId) {
      const isProductOutOfStock = skuItemInfo.filter((item: any) => item.stockQuantity > 0).length === 0

      if (isProductOutOfStock) {
        toast.warning(t('LiveRoom_Product_OutOfStuck') as string, { toastId: `noStock-${selectedProductDataId}` })

        setSelectedProductData(defaultProductData)
      }
    }
  }, [skuItemInfo, selectedProductData])

  useEffect(() => {
    if (skuItemInfo?.length > 1) {
      specPopupRef.current.setIsPopupOpen(true)
    } else if (skuItemInfo?.length === 1 && filteredItems.length === 1) {
      handleAddCartOrBuy({ quantity: 1 })
    }
  }, [JSON.stringify(skuItemInfo), JSON.stringify(filteredItems)])

  useEffect(() => {
    const newProductList = productList
      ?.map((item: any) => {
        // 依據 socket 的 topProductList 跟 publishProductList 判斷 isTop 跟 isDel
        const isTopBySocket = topProductList.includes(item?.productInfo?.productId)
        const isPublishBySocket = publishProductList.includes(item?.productInfo?.productId)

        // webview 沒辦法跟 socket 溝通，所以 isClientSideShopApp 就用原本的直不用 socket
        return {
          ...item,
          isTop: isClientSideShopApp ? item.isTop : isTopBySocket,
          isDel: isClientSideShopApp ? item.isDel : !isPublishBySocket,
          productInfo: item.productInfo,
        }
      })
      ?.sort((a: any, b: any) => {
        // isTop 排前面再依據 sort 排序
        if (a.isTop && !b.isTop) {
          return -1
        } else if (!a.isTop && b.isTop) {
          return 1
        } else {
          return a.sort - b.sort
        }
      })
    isProductListSortedRef.current = true
    setCurrentProductList(newProductList)
  }, [productList, topProductList, publishProductList, isClientSideShopApp])

  const handleAddCartClick = (event: any, productId: string) => {
    event.stopPropagation()
    trackLiveEvent({ type: LIVE_TRACK_EVENT.PRODUCT_CLICK, roomId, productId: Number(productId) || undefined })

    setSelectedProductData({ productId, specPopupAction: SPEC_ACTION.ADD_CART })
  }

  const handleBuyNowClick = (event: any, productId: string) => {
    event.stopPropagation()
    trackLiveEvent({ type: LIVE_TRACK_EVENT.PRODUCT_CLICK, roomId, productId: Number(productId) || undefined })

    setSelectedProductData({ productId, specPopupAction: SPEC_ACTION.BUY_NOW })
  }

  const handleAddCartOrBuy = ({ quantity }: { quantity: number }) => {
    const currentProduct = { productId: productInfo?.productId || 0, name: productInfo.productName || '' }
    const userData = { nickName, headshotUrl, role, userToken: userToken || shoppingcartId } // userToken 有登入就用 userToken 沒登入就用 shoppingcartId
    const currentSku = filteredItems[0]
    const currentSkuId = currentSku?.skuId

    const { productName, productId, merchantName, categoryName, currency, categoryId } = productInfo as ProductInfoType
    // 隱藏商品不送 add_to_cart
    const gtmUpdateCartData: THandleGtmUpdateCart | undefined = productInfo?.isHidden
      ? undefined
      : {
          event: GtmUpdateCartAction.ADD,
          currency: currency,
          items: [
            {
              itemName: productName,
              itemId: productId,
              itemBrand: merchantName,
              itemCategory: categoryName,
              itemVarient: currentSku.attrDescp,
              quantity: quantity,
              price: displayPrice,
              itemListName: categoryName,
              itemListId: categoryId,
            },
          ],
        }

    if (selectedProductData.specPopupAction === SPEC_ACTION.ADD_CART) {
      handleCartDataUpdate({
        t,
        dispatch,
        action: CartAction.ADD,
        onStockInsufficient: handleStockInsufficient,
        quantity,
        roomId,
        skuId: currentSkuId,
        skuList: [
          {
            skuId: currentSkuId,
            quantity,
            roomId,
            referralFrom,
            referralId,
          },
        ],
        successCallback: () => {
          handleAddCartMessage({ socket, item: currentProduct, userData })
          trackLiveEvent({ type: LIVE_TRACK_EVENT.ADD_TO_CART, roomId, productId: Number(currentProduct.productId) || undefined, quantity })
          handleSpecPopupClose()
        },
        errorCallback: () => handleSpecPopupClose(),
        gtmUpdateCartData,
      })
    } else {
      if (isLogin || isNativeApp || isClientSideShopApp) {
        const fastCheckoutItemPayload = {
          merchantId: productInfo?.merchantId,
          skuId: filteredItems[0]?.skuId,
          quantity: productAmount,
        }

        handleCartDataUpdate({
          t,
          dispatch,
          action: CartAction.ADD,
          onStockInsufficient: handleStockInsufficient,
          skuList: [
            {
              skuId: filteredItems[0]?.skuId,
              quantity,
              roomId,
              referralFrom,
              referralId,
            },
          ],
          successCallback: () => {
            handleAddCartMessage({ socket, item: currentProduct, userData })
            trackLiveEvent({ type: LIVE_TRACK_EVENT.BUY_NOW, roomId, productId: Number(currentProduct.productId) || undefined, quantity })

            if (isClientSideShopApp) {
              window.location.href = 'tutorshop://main?tab=cart'
            } else {
              fastCheckout({ router, dispatch, fastCheckoutItem: fastCheckoutItemPayload })
            }
          },
          errorCallback: () => setSelectedProductData(defaultProductData),
          gtmUpdateCartData,
        })
      } else {
        specPopupRef.current.setIsPopupOpen(false)
        loginPopupRef.current.setIsPopupOpen(true)
      }
    }
  }

  const handleSpecPopupConfirm = () => {
    handleAddCartOrBuy({ quantity: productAmount })
  }

  const handleSpecPopupClose = () => {
    setSelectedProductData(defaultProductData)

    specPopupRef.current.setIsPopupOpen(false)
  }

  const handleRedirectToLoginPage = () => {
    // 走這情境不會選規格，所以數量直接寫死 1
    setBuyNowCacheData({ productInfo, filteredItems, productAmount: 1, roomId, referralFrom, referralId })

    handleLoginOrRegister({ isLogin: true, router, setUserRecordMutation, fixedPlayerData, isGoRedirect: true, gtmLocation: GTM_LOCATION.LIVE_ROOM })
  }

  return (
    <div className={cx('productList', className)}>
      {!isClientSideShopApp && (
        <>
          <p className={cx('productList-title')}>{t('Common_ProductListCount', { productListCount: currentProductList?.length ?? 0 })}</p>
          <p className={cx('productList-description')}>{t('LiveRoom_ProductList_Description')}</p>
        </>
      )}

      <div className={cx('productList-list')}>
        {currentProductList
          ?.sort((a: any, b: any) => {
            return a.isDel - b.isDel
          })
          ?.map((item: any, index: any) => {
            return isShowHostProduct ? (
              <HostProduct className={cx('productList-list-item')} key={index} {...item} isLive={isLive} roomId={roomId} />
            ) : (
              <RoomProduct
                className={cx('productList-list-item')}
                key={index}
                {...item}
                isLive={isLive}
                handleAddCartClick={handleAddCartClick}
                handleBuyNowClick={handleBuyNowClick}
                observer={observerRef.current}
                gtmItemEventParams={{ itemListName: GtmItemListName.LIVE_ROOM, itemListId: roomId }}
                liveRoomId={roomId}
                pipWithWinOpen={pipWithWinOpen}
                handlePromotionMessage={handlePromotionMessage}
              />
            )
          })}
      </div>

      <Popup
        wrapperClassname={cx('productList-spec-popup')}
        ref={specPopupRef}
        onConfirm={handleSpecPopupConfirm}
        onCancel={handleSpecPopupClose}
        confirmLabel={selectedProductData.specPopupAction === SPEC_ACTION.ADD_CART ? t('Common_AddToCart') : t('Common_PayNow')}
        isDisabledConfirm={productAmount === 0}
        isDrawer
        customContainer={isFullScreen ? popupContainer : null}
      >
        <SpecContent
          className={cx('productList-spec-content')}
          menu={menu}
          filteredItems={filteredItems}
          searchKeyList={searchKey}
          skuItemInfo={skuItemInfo}
          handleSearchKeyChange={handleSearchKeyChange}
          setProductAmount={setProductAmount}
          productInfo={productInfo}
          isLive={isLive}
          price={displayPrice}
          title={isLiveMallClub && <div className={cx('productList-spec-content-title')}>{t('ShoppingCart_Spec_Content_Title')}</div>}
        />
      </Popup>

      <Popup
        ref={loginPopupRef}
        onConfirm={handleRedirectToLoginPage}
        onCancel={() => {
          setSelectedProductData(defaultProductData)
          loginPopupRef.current.setIsPopupOpen(false)
        }}
        title={t('Common_Login_Before_Buy')}
        confirmLabel={t('Common_Login')}
        cancelLabel={t('Common_Cancel')}
      />
    </div>
  )
}

export default ProductList
